import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCostPriceUpdateType } from "./enums/enumCostPriceUpdateType.generated";
import { EnumPurchaseOrderDeliveryDisbursement } from "./enums/enumPurchaseOrderDeliveryDisbursement.generated";

// Model Objects

export class TenantSettings extends ModelObject<TenantSettings> {

    public version: number = 0;
    public tenantId: Guid = Guid.createEmpty();
    /**
    * Controls the Mega Batches status; when true, Production is disabled.
    */
    public isMegaBatchesEnabled: boolean = false;
    /**
    * Specifies the duration in months for which a product batch can be displayed in the system.
    * Minimum : 1
    */
    public productBatchDuration: number = 0;
    /**
    * If enabled, allows hop alpha for materials.
    */
    public usesHopAlpha: boolean = false;
    public poDefaultDeliveryDisbursement: EnumPurchaseOrderDeliveryDisbursement = EnumPurchaseOrderDeliveryDisbursement.none;
    /**
    * If enabled, allows regional tax for outlets and calculate additional tax to orders.
    */
    public usesRegionalTax: boolean = false;
    /**
    * Default Cost Price update to use for prefilling update dialog. : 0 = KeepCurrent, 1 = LatestKnown, 2 = RollingAverage
    */
    public costPriceUpdateType: EnumCostPriceUpdateType = EnumCostPriceUpdateType.keepCurrent;
    /**
    * If enabled, shows the tenant indicator on the UI. Useful for groups of affilliated tenants.
    */
    public showTenantIndicator: boolean = false;
    public tenantIndicatorColour: string = "";
    public tenantIndicatorText: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.isMegaBatchesEnabled = jsonSerializedObject.isMegaBatchesEnabled;
          this.productBatchDuration = jsonSerializedObject.productBatchDuration;
          this.usesHopAlpha = jsonSerializedObject.usesHopAlpha;
          this.poDefaultDeliveryDisbursement = EnumPurchaseOrderDeliveryDisbursement.getByValue(jsonSerializedObject.poDefaultDeliveryDisbursement, this.poDefaultDeliveryDisbursement);
          this.usesRegionalTax = jsonSerializedObject.usesRegionalTax;
          this.costPriceUpdateType = EnumCostPriceUpdateType.getByValue(jsonSerializedObject.costPriceUpdateType, this.costPriceUpdateType);
          this.showTenantIndicator = jsonSerializedObject.showTenantIndicator;
          this.tenantIndicatorColour = jsonSerializedObject.tenantIndicatorColour;
          this.tenantIndicatorText = jsonSerializedObject.tenantIndicatorText;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.tenantId = this.tenantId.toString();
        toRet.isMegaBatchesEnabled = this.isMegaBatchesEnabled;
        toRet.productBatchDuration = this.productBatchDuration;
        toRet.usesHopAlpha = this.usesHopAlpha;
        toRet.poDefaultDeliveryDisbursement = this.poDefaultDeliveryDisbursement.value;
        toRet.usesRegionalTax = this.usesRegionalTax;
        toRet.costPriceUpdateType = this.costPriceUpdateType.value;
        toRet.showTenantIndicator = this.showTenantIndicator;
        toRet.tenantIndicatorColour = this.tenantIndicatorColour;
        toRet.tenantIndicatorText = this.tenantIndicatorText;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettings.TenantId cannot be empty"},
            {field: "productBatchDuration", rule: (v: any) => v == null || v >= 1 || "ProductBatchDuration is not greater than minimum allowed value: 1"},
            {field: "productBatchDuration", rule: (v: any) => v == null || v <= 2147483647 || "ProductBatchDuration is above maximum allowed value: 2147483647"},
    ];
}


export class TenantSettingsService extends ServiceBase {

  public async getTenantSettings(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TenantSettings> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => new TenantSettings(response.tenantSettings);;
    return this.makeJsonRequest("tenantSettings/v1/getTenantSettings", req, false, extractor, opts);
  };

  public async setIsMegaBatchesEnabled(tenantId: Guid, enable: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    enable:enable,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/setIsMegaBatchesEnabled", req, true, extractor, opts);
  };

  public async setProductBatchDuration(tenantId: Guid, monthsToDisplay: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    monthsToDisplay:monthsToDisplay,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/setProductBatchDuration", req, true, extractor, opts);
  };

  public async savePurchaseOrderSettings(tenantId: Guid, poDefaultDeliveryDisbursement: EnumPurchaseOrderDeliveryDisbursement, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    poDefaultDeliveryDisbursement:poDefaultDeliveryDisbursement.value,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/savePurchaseOrderSettings", req, true, extractor, opts);
  };

  public async saveProductionSettings(tenantId: Guid, usesHopAlpha: boolean, costPriceUpdateType: EnumCostPriceUpdateType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    usesHopAlpha:usesHopAlpha,
    costPriceUpdateType:costPriceUpdateType.value,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveProductionSettings", req, true, extractor, opts);
  };

  public async saveTaxSettings(tenantId: Guid, usesRegionalTax: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    usesRegionalTax:usesRegionalTax,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveTaxSettings", req, true, extractor, opts);
  };

  public async saveDisplaySettings(tenantId: Guid, showTenantIndicator: boolean, tenantIndicatorColour: string, tenantIndicatorText: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    showTenantIndicator:showTenantIndicator,
    tenantIndicatorColour:tenantIndicatorColour,
    tenantIndicatorText:tenantIndicatorText,
    };
    const extractor = null;
    return this.makeJsonRequest("tenantSettings/v1/saveDisplaySettings", req, true, extractor, opts);
  };
}

export const tenantSettingsService = new TenantSettingsService();